import { useState, useEffect } from 'react';
import api, { formatIQD, formatUSD } from '../utils/api';
import { parseArabicToEnglish } from '../utils/formatters';
import toast from 'react-hot-toast';

const C = { navy: 'var(--text-dark-h, #0F1E33)', teal: '#17A589', blue: '#9B8EC4', red: '#E74C3C', orange: '#E67E22', green: '#27AE60', border: '#DDE2E8', gray: '#F0F4F8' };

const empty = { name: '', description: '', included_seconds: 300, price_usd: '', price_iqd: '', overage_per_sec_usd: 1, overage_per_sec_iqd: 1500, includes_influencer: false, includes_management: false, free_edits_per_video: 1, validity_days: 30 };

function Field({ label, children }) {
  return (
    <div>
      <label className="dark:!text-gray-400" style={{ fontSize: 12, color: '#7F8C8D', display: 'block', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

const inp = { width: '100%', padding: '8px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontFamily: 'Tajawal, sans-serif', fontSize: 14, boxSizing: 'border-box' };

export default function PackagesPage() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/packages');
      setPackages(res.data);
    } catch { toast.error('خطأ في التحميل'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(empty); setEditing(null); setShowForm(true); };
  const openEdit = (pkg) => {
    setForm({ ...pkg, includes_influencer: !!pkg.includes_influencer, includes_management: !!pkg.includes_management });
    setEditing(pkg.id);
    setShowForm(true);
  };

  // حساب IQD تلقائياً من USD
  const handleUsdChange = (val) => {
    const usd = parseFloat(val) || 0;
    setForm(f => ({ ...f, price_usd: val, price_iqd: Math.round(usd * 1500) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/packages/${editing}`, form);
        toast.success('تم تحديث الباقة ✅');
      } else {
        await api.post('/packages', form);
        toast.success('تم إنشاء الباقة ✅');
      }
      setShowForm(false);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'خطأ'); }
    setSaving(false);
  };

  const handleDelete = async (pkg) => {
    if (!window.confirm(`حذف باقة "${pkg.name}"؟`)) return;
    try {
      await api.delete(`/packages/${pkg.id}`);
      toast.success('تم الحذف');
      load();
    } catch { toast.error('خطأ'); }
  };

  const toggleActive = async (pkg) => {
    try {
      await api.put(`/packages/${pkg.id}`, { ...pkg, is_active: pkg.is_active ? 0 : 1, includes_influencer: pkg.includes_influencer ? 1 : 0, includes_management: pkg.includes_management ? 1 : 0 });
      toast.success(pkg.is_active ? 'تم تعطيل الباقة' : 'تم تفعيل الباقة');
      load();
    } catch { toast.error('خطأ'); }
  };

  return (
    <div style={{ padding: 24, fontFamily: 'Tajawal, sans-serif', direction: 'rtl' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 className="dark:!text-gray-100" style={{ margin: 0, color: C.navy, fontSize: 24 }}>إدارة الباقات</h1>
          <p className="dark:!text-gray-400" style={{ margin: '4px 0 0', color: '#7F8C8D', fontSize: 14 }}>أنشئ وعدّل باقاتك بالكامل</p>
        </div>
        <button onClick={openAdd} style={{ background: C.teal, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 15, cursor: 'pointer', fontFamily: 'Tajawal, sans-serif' }}>
          + باقة جديدة
        </button>
      </div>

      {/* Cards */}
      {loading ? <p className="dark:!text-gray-400" style={{ textAlign: 'center', color: '#7F8C8D', padding: 40 }}>جار التحميل...</p> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {packages.length === 0 && (
            <div className="dark:!text-gray-400" style={{ gridColumn: '1/-1', textAlign: 'center', padding: 60, color: '#7F8C8D' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
              <p>لا توجد باقات — أضف أول باقة!</p>
            </div>
          )}
          {packages.map(pkg => (
            <div key={pkg.id} className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!shadow-none" style={{ background: 'var(--bg-flat-white, #fff)', borderRadius: 12, border: `1px solid ${pkg.is_active ? C.border : 'var(--border-light, #FADBD8)'}`, padding: 20, opacity: pkg.is_active ? 1 : 0.7 }}>
              {/* Name & Status */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <h3 className="dark:!text-gray-100" style={{ margin: 0, color: C.navy, fontSize: 17 }}>{pkg.name}</h3>
                  {pkg.description && <p className="dark:!text-gray-400" style={{ margin: '4px 0 0', color: '#7F8C8D', fontSize: 13 }}>{pkg.description}</p>}
                </div>
                <span style={{ background: pkg.is_active ? '#EAFAF1' : '#FDEDEC', color: pkg.is_active ? C.green : C.red, padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700 }}>
                  {pkg.is_active ? '● نشط' : '○ معطّل'}
                </span>
              </div>

              {/* Details */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${C.gray}` }}>
                  <span className="dark:!text-gray-400" style={{ color: '#7F8C8D' }}>السعر</span>
                  <strong className="dark:!text-gray-100" style={{ color: C.navy }}>{formatUSD(pkg.price_usd)} / {formatIQD(pkg.price_iqd || 0)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${C.gray}` }}>
                  <span className="dark:!text-gray-400" style={{ color: '#7F8C8D' }}>الثواني المشمولة</span>
                  <strong>{pkg.included_seconds} ثانية</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${C.gray}` }}>
                  <span className="dark:!text-gray-400" style={{ color: '#7F8C8D' }}>الصلاحية</span>
                  <strong>{pkg.validity_days || 30} يوم</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${C.gray}` }}>
                  <span className="dark:!text-gray-400" style={{ color: '#7F8C8D' }}>سعر التجاوز</span>
                  <strong>${pkg.overage_per_sec_usd}/ثانية</strong>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 4 }}>
                  {pkg.includes_influencer ? <span style={{ background: '#EBF5FB', color: '#2E86C1', padding: '2px 8px', borderRadius: 8, fontSize: 11 }}>✅ AI Influencer</span> : null}
                  {pkg.includes_management ? <span style={{ background: '#E8F8F5', color: '#17A589', padding: '2px 8px', borderRadius: 8, fontSize: 11 }}>✅ إدارة حسابات</span> : null}
                  <span className="dark:!text-gray-400" style={{ background: C.gray, color: '#7F8C8D', padding: '2px 8px', borderRadius: 8, fontSize: 11 }}>
                    {pkg.free_edits_per_video >= 99 ? 'تعديلات غير محدودة' : `${pkg.free_edits_per_video} تعديل/فيديو`}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => openEdit(pkg)}
                  className="dark:!bg-blue-500/20 dark:!border-blue-500/30 dark:!text-blue-300"
                  style={{ flex: 1, padding: '8px', background: C.blue + '15', color: C.blue, border: `1px solid ${C.blue}`, borderRadius: 6, cursor: 'pointer', fontFamily: 'Tajawal, sans-serif', fontSize: 13 }}>
                  تعديل
                </button>
                <button onClick={() => toggleActive(pkg)}
                  className={pkg.is_active ? "dark:!bg-warning/20 dark:!border-warning/30 dark:!text-warning" : "dark:!bg-success/20 dark:!border-success/30 dark:!text-success"}
                  style={{ padding: '8px 12px', background: pkg.is_active ? '#FEF9E7' : '#EAFAF1', color: pkg.is_active ? C.orange : C.green, border: `1px solid ${pkg.is_active ? C.orange : C.green}`, borderRadius: 6, cursor: 'pointer', fontFamily: 'Tajawal, sans-serif', fontSize: 12 }}>
                  {pkg.is_active ? 'تعطيل' : 'تفعيل'}
                </button>
                <button onClick={() => handleDelete(pkg)}
                  className="dark:!bg-danger/20 dark:!border-danger/30 dark:!text-danger"
                  style={{ padding: '8px 12px', background: '#FDEDEC', color: C.red, border: `1px solid ${C.red}`, borderRadius: 6, cursor: 'pointer', fontFamily: 'Tajawal, sans-serif', fontSize: 12 }}>
                  حذف
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Form */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-white, #fff)', borderRadius: 12, padding: 28, width: 560, maxHeight: '90vh', overflowY: 'auto', direction: 'rtl' }}>
            <h2 className="dark:!text-gray-100" style={{ margin: '0 0 20px', color: C.navy }}>{editing ? 'تعديل الباقة' : 'باقة جديدة'}</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>

                <div style={{ gridColumn: '1/-1' }}>
                  <Field label="اسم الباقة *">
                    <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="مثال: باقة أساسية" style={inp} />
                  </Field>
                </div>

                <div style={{ gridColumn: '1/-1' }}>
                  <Field label="وصف (اختياري)">
                    <input value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="مثال: 10 فيديو شهرياً" style={inp} />
                  </Field>
                </div>

                <Field label="السعر بالدولار ($) *">
                  <input type="text" inputMode="decimal" value={form.price_usd} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => handleUsdChange(e.target.value)} required placeholder="250" style={inp} />
                </Field>

                <Field label="السعر بالدينار (د.ع)">
                  <input type="text" inputMode="decimal" value={form.price_iqd} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setForm({ ...form, price_iqd: e.target.value })} placeholder="375000" style={inp} />
                </Field>

                <Field label="الثواني المشمولة">
                  <input type="text" inputMode="decimal" value={form.included_seconds} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setForm({ ...form, included_seconds: parseInt(e.target.value) || 0 })} placeholder="300" style={inp} />
                </Field>

                <Field label="مدة الصلاحية (يوم)">
                  <input type="text" inputMode="decimal" value={form.validity_days} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setForm({ ...form, validity_days: parseInt(e.target.value) || 30 })} placeholder="30" style={inp} />
                </Field>

                <Field label="سعر التجاوز ($/ثانية)">
                  <input type="text" inputMode="decimal" value={form.overage_per_sec_usd} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setForm({ ...form, overage_per_sec_usd: parseFloat(e.target.value) || 1 })} style={inp} />
                </Field>

                <Field label="تعديلات مجانية/فيديو">
                  <input type="text" inputMode="decimal" value={form.free_edits_per_video} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setForm({ ...form, free_edits_per_video: parseInt(e.target.value) || 1 })} style={inp} />
                </Field>

                {/* Checkboxes */}
                <div style={{ gridColumn: '1/-1', background: C.gray, borderRadius: 8, padding: 12, display: 'flex', gap: 20 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                    <input type="checkbox" checked={form.includes_influencer} onChange={e => setForm({ ...form, includes_influencer: e.target.checked })} />
                    AI Influencer مشمول مجاناً
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                    <input type="checkbox" checked={form.includes_management} onChange={e => setForm({ ...form, includes_management: e.target.checked })} />
                    إدارة حسابات مشمولة
                  </label>
                </div>

                {/* Preview */}
                <div style={{ gridColumn: '1/-1', background: '#EBF5FB', borderRadius: 8, padding: 12, fontSize: 13 }}>
                  <strong style={{ color: C.blue }}>معاينة: </strong>
                  {form.name || 'اسم الباقة'} — {formatUSD(form.price_usd || 0)} / {formatIQD(parseInt(form.price_iqd) || 0)} — {form.included_seconds}ث — {form.validity_days} يوم
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowForm(false)}
                  style={{ padding: '9px 20px', border: `1px solid ${C.border}`, borderRadius: 8, background: 'var(--bg-flat-white, #fff)', cursor: 'pointer', fontFamily: 'Tajawal, sans-serif' }}>
                  إلغاء
                </button>
                <button type="submit" disabled={saving}
                  style={{ padding: '9px 24px', background: C.teal, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontFamily: 'Tajawal, sans-serif', fontSize: 14 }}>
                  {saving ? 'جار الحفظ...' : editing ? 'حفظ التعديلات' : 'إنشاء الباقة'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
